/**
 * Single source of truth for the Klaviyo webhook payload builder.
 *
 * Both the Setup wizard (Setup.tsx) and the Help/FAQ modal (KlaviyoSetupModal)
 * import from here so they always emit identical JSON.
 *
 * IMPORTANT: the JSON *keys* (`fieldName`) must stay canonical — the worker's
 * `normalize_klaviyo_payload` parses `first_name`, `last_name`,
 * `email`/`email_address`, `klaviyo_id`/`customer_id`. Only the right-hand
 * Klaviyo variable (the `{{ person|lookup:'…' }}` token) is user-editable.
 */

export type PrimaryIdentifier = 'klaviyo_id' | 'email' | 'uid';

export interface FieldOption {
  /** Stable id used in selection sets + override maps. */
  id: string;
  label: string;
  description: string;
  /** Canonical JSON key sent to the webhook — MUST match what the worker parses. */
  fieldName: string;
  /** Default Klaviyo lookup variable (right-hand side, without the `$`-less wrapping). */
  lookupVar: string;
  group: 'Contact' | 'Location';
  /**
   * Whether enrichment actually consumes this field. Location/phone fields are
   * pass-through only (echoed into the output CSV, never used to enrich).
   */
  usedForEnrichment: boolean;
}

/**
 * Per-feature credit prices — mirrors the canonical backend copy in
 * worker/services/credit_calculator.py (FEATURE_CREDITS). Keep in sync.
 */
export const FEATURE_CREDITS = {
  capitalization: 3, // Name Cleanup
  nameParsingFromEmail: 5, // Name Cleanup plus
  age: 2, // Birthyear
  nameHoliday: 2, // Name Day
} as const;

export const FIELD_OPTIONS: FieldOption[] = [
  {
    id: 'klaviyo_id',
    label: 'Klaviyo profile ID',
    description: 'Primary identifier for the contact',
    fieldName: 'klaviyo_id',
    lookupVar: '$id',
    group: 'Contact',
    usedForEnrichment: false,
  },
  {
    id: 'email',
    label: 'Email address',
    description: 'Used for enrichment lookups',
    fieldName: 'email',
    lookupVar: '$email',
    group: 'Contact',
    usedForEnrichment: true,
  },
  {
    id: 'first_name',
    label: 'First name',
    description: "Contact's given name",
    fieldName: 'first_name',
    lookupVar: '$first_name',
    group: 'Contact',
    usedForEnrichment: true,
  },
  {
    id: 'last_name',
    label: 'Last name',
    description: "Contact's family name",
    fieldName: 'last_name',
    lookupVar: '$last_name',
    group: 'Contact',
    usedForEnrichment: true,
  },
  {
    id: 'phone',
    label: 'Phone number',
    description: 'Optional — passed through to output, not used for enrichment',
    fieldName: 'phone_number',
    lookupVar: '$phone_number',
    group: 'Contact',
    usedForEnrichment: false,
  },
  {
    id: 'city',
    label: 'City',
    description: 'Optional — passed through to output, not used for enrichment',
    fieldName: 'city',
    lookupVar: '$city',
    group: 'Location',
    usedForEnrichment: false,
  },
  {
    id: 'region',
    label: 'Region / State',
    description: 'Optional — passed through to output, not used for enrichment',
    fieldName: 'region',
    lookupVar: '$region',
    group: 'Location',
    usedForEnrichment: false,
  },
  {
    id: 'country',
    label: 'Country',
    description: 'Optional — passed through to output, not used for enrichment',
    fieldName: 'country',
    lookupVar: '$country',
    group: 'Location',
    usedForEnrichment: false,
  },
  {
    id: 'zip',
    label: 'ZIP / Postal code',
    description: 'Optional — passed through to output, not used for enrichment',
    fieldName: 'zip',
    lookupVar: '$zip',
    group: 'Location',
    usedForEnrichment: false,
  },
];

/** Fields ticked by default in the payload builder. */
export const DEFAULT_SELECTED_FIELDS = ['klaviyo_id', 'email', 'first_name', 'last_name'];

/**
 * Pre-filled, editable field names for the data-input step (F-19). Klaviyo uses
 * its official property names ($-prefixed, except `email`); other platforms get
 * neutral sensible defaults the user is expected to correct to match their ESP.
 */
const KLAVIYO_FIELD_DEFAULTS: Record<string, string> = {
  klaviyo_id: '$id',
  email: 'email',
  first_name: '$first_name',
  last_name: '$last_name',
  phone: '$phone_number',
  city: '$city',
  region: '$region',
  country: '$country',
  zip: '$zip',
};

const GENERIC_FIELD_DEFAULTS: Record<string, string> = {
  email: 'email',
  first_name: 'firstname',
  last_name: 'lastname',
  phone: 'phone',
  city: 'city',
  region: 'region',
  country: 'country',
  zip: 'zip',
};

/** Default platform field name for a data-input field id. */
export function defaultFieldName(id: string, isKlaviyo: boolean): string {
  const map = isKlaviyo ? KLAVIYO_FIELD_DEFAULTS : GENERIC_FIELD_DEFAULTS;
  return map[id] ?? id;
}

export interface PrimaryKeyOption {
  id: PrimaryIdentifier;
  label: string;
  description: string;
  /** Field id that must be present in the payload for this PK (uid needs none). */
  requiresFieldId: string | null;
}

export const PRIMARY_KEY_OPTIONS: PrimaryKeyOption[] = [
  {
    id: 'klaviyo_id',
    label: 'Klaviyo profile ID',
    description: "Use Klaviyo's profile id as the primary key",
    requiresFieldId: 'klaviyo_id',
  },
  {
    id: 'email',
    label: 'Email address',
    description: 'Use the contact email as the primary key',
    requiresFieldId: 'email',
  },
  {
    id: 'uid',
    label: 'Our UID',
    description: 'KNOX assigns a unique id — no source field needed',
    requiresFieldId: null,
  },
];

export function getFieldById(id: string): FieldOption | undefined {
  return FIELD_OPTIONS.find((f) => f.id === id);
}

/** A user-added field (U-08): canonical JSON key + the platform lookup variable. */
export interface ExtraPayloadField {
  key: string;
  lookupVar: string;
}

/**
 * Build the webhook JSON body.
 *
 * @param selectedIds  field ids to include (insertion order follows FIELD_OPTIONS)
 * @param varOverrides optional map of field id → custom lookup variable. The
 *                     override IS the platform field name the user typed on the
 *                     data-input step (U-05/U-10), so editing there flows here.
 * @param extraFields  user-added fields (U-08), appended after the known fields.
 */
export function generatePayload(
  selectedIds: Iterable<string>,
  varOverrides: Record<string, string> = {},
  extraFields: ExtraPayloadField[] = [],
): string {
  const set = selectedIds instanceof Set ? selectedIds : new Set(selectedIds);
  const fields = FIELD_OPTIONS.filter((f) => set.has(f.id));
  const extras = extraFields.filter((e) => e.key.trim());
  if (fields.length === 0 && extras.length === 0) return '{\n}';

  const entries: { name: string; lookup: string }[] = [
    ...fields.map((f) => ({
      name: f.fieldName,
      lookup: (varOverrides[f.id] ?? f.lookupVar).trim() || f.lookupVar,
    })),
    ...extras.map((e) => ({
      name: e.key.trim(),
      lookup: e.lookupVar.trim() || e.key.trim(),
    })),
  ];

  const lines = entries.map((e, i) => {
    const comma = i < entries.length - 1 ? ',' : '';
    return `  "${e.name}": "{{ person|lookup:'${e.lookup}' }}"${comma}`;
  });
  return `{\n${lines.join('\n')}\n}`;
}
